import React, {useEffect, useMemo, useState} from 'react';

import {format} from 'date-fns';
import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {useLocation, useParams} from 'react-router-dom';

import {fetchDocumentInvoiceQR} from 'api';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {CURRENCY} from 'core/constants/currency';
import {COUNTRY, IS_NG} from 'core/constants/env';
import {Locales} from 'core/localization';
import {CreateInvoiceGate, setInvoiceState} from 'models/documents/invoices/common';
import {
    $infoValues,
    $invoiceLineValues,
    $pageValues,
    $taxesAndTotal,
} from 'models/documents/invoices/create';

import {GridFieldConfig} from 'types';
import {handleNil} from 'utils/handleNil';

import {VAT_CATEGORY} from '../helpers/consts';

import {ChargesList} from '../Pages/TaxesTotal/components/ChargesTable';

import {
    BlockQR,
    Cell,
    ChargesCell,
    Container,
    Divider,
    Header,
    Label,
    List,
    MarkedTag,
    Row,
    Summary,
    Title,
    Wrapper,
} from './styled';

export const InvoicePrint = () => {
    const {id, invoiceId}: {id?: string; invoiceId?: string} = useParams();
    useEffect(() => {
        setInvoiceState(invoiceId ? 'new' : id ?? null);

        return () => {
            CreateInvoiceGate.close({});
        };
    }, []);

    const {i18n, t: translation} = useTranslation();
    const BASE_LOCALE = 'pages.documents.invoices.create';
    const INVOICE_LINES = `${BASE_LOCALE}.invoiceLines`;
    const GENERAL_INFO = `${BASE_LOCALE}.generalInformation`;

    const location = useLocation();

    useEffect(() => {
        const lang = new URLSearchParams(location.search).get('lang');
        if (!lang) return;
        if (lang !== i18n.language) i18n.changeLanguage(lang);
    }, [location.search, i18n]);

    const [QR, setQR] = useState('');
    const invoiceLines = useStore($invoiceLineValues);
    const infoValues = useStore($infoValues);
    const pageValues = useStore($pageValues);
    const taxesAndTotal = useStore($taxesAndTotal);

    useEffect(() => {
        if (!IS_NG || !infoValues.irn) return;
        fetchDocumentInvoiceQR(infoValues.irn)
            .then(setQR)
            .catch(console.error);
    }, [infoValues.irn]);

    const DATE_PATTERN = 'yyyy-MM-dd';
    const formatDate = (date?: Date | string) => {
        if (!date) return formatValue(date);
        return format(new Date(date), DATE_PATTERN);
    };
    const formatValue = (v?: any) => {
        if (!v) return '—';
        return v;
    };

    const formatNumberWithFraction = (v?: any, options: Intl.NumberFormatOptions = {}) => {
        if (!['number', 'string'].includes(typeof v)) return '—';
        return Number(v).toLocaleString(i18n.language === Locales.bs ? 'bs-BA' : 'en-US', {
            minimumFractionDigits: 2,
            ...options,
        });
    };

    const t = (key: string, vars?: Record<string, any>) =>
        translation(`${INVOICE_LINES}.${key}`, vars ?? {});

    const invoicesColumnNames: Record<string, {id: string; label: string}> = {
        lineId: {id: 'lineId', label: '№'},
        description: {id: 'description', label: t('productName')},
        codeGtin: {id: 'codeGtin', label: t('markingCode')},
        serviceCategory: {id: 'serviceCategory', label: t('serviceCategory')},
        qty: {id: 'qty', label: t('qty')},
        price: {id: 'price', label: `${t('price')} (${CURRENCY})`},
        charges: {id: 'charges', label: t('charges')},
        lines: {id: 'lines', label: t('lines')},
        vatCategory: {id: 'vatCategory', label: t('vatCategory')},
        vat: {id: 'vat', label: t('vat')},
        lineAmountWVat: {id: 'lineAmountWVat', label: t('lineAmountWVat', {currency: CURRENCY})},
    };
    const invoicesColumnSettings: GridFieldConfig[] = useMemo(
        () => [
            {
                id: invoicesColumnNames.lineId.id,
                header: invoicesColumnNames.lineId.label,
                width: 50,
            },
            {
                id: invoicesColumnNames.codeGtin.id,
                header: (
                    <div>
                        {t('markingCode')} | {t('unmarkedIds')}
                    </div>
                ),
                width: 200,
            },
            {
                id: invoicesColumnNames.description.id,
                header: (
                    <div>
                        <div>{t('nameOfProduct')}</div>
                        <div>{t('description')}</div>
                    </div>
                ),
                width: 200,
            },
            {
                id: invoicesColumnNames.serviceCategory.id,
                header: invoicesColumnNames.serviceCategory.label,
                width: 142,
            },
            {id: invoicesColumnNames.qty.id, header: invoicesColumnNames.qty.label, width: 64},
            {
                id: invoicesColumnNames.price.id,
                header: invoicesColumnNames.price.label,
                width: 102,
            },
            ...(invoiceLines.some((line) => !!line.charges)
                ? [
                      {
                          id: invoicesColumnNames.charges.id,
                          header: invoicesColumnNames.charges.label,
                          width: 232,
                      },
                  ]
                : []),
            {id: invoicesColumnNames.lines.id, header: invoicesColumnNames.lines.label, width: 112},
            {
                id: invoicesColumnNames.vatCategory.id,
                header: invoicesColumnNames.vatCategory.label,
                width: 100,
            },
            {id: invoicesColumnNames.vat.id, header: invoicesColumnNames.vat.label, width: 62},
            {
                id: invoicesColumnNames.lineAmountWVat.id,
                header: t('lineAmountWVat', {currency: CURRENCY}),
                width: 102,
            },
        ],
        [t, invoiceLines],
    );
    const invoiceColumns = useMemo(() => mapTableColumns(invoicesColumnSettings), [
        invoicesColumnSettings,
    ]);
    const invoicesData: Record<string, any>[] = useMemo(() => {
        if (!invoiceLines) return [];
        return invoiceLines.map((elem, id) => ({
            lineId: <Cell>{id + 1}</Cell>,
            description: <Cell>{elem.description}</Cell>,
            codeGtin: (
                <div>
                    {elem.isMarkedItem ? (
                        <div>
                            <MarkedTag>{t('marked')}</MarkedTag>
                            {handleNil(elem.code)}
                            <br />
                            {handleNil(elem.gtin)}
                        </div>
                    ) : (
                        <div>
                            <MarkedTag>{t('unmarked')}</MarkedTag>
                            {handleNil(elem.sellersItemId)}
                            <br />
                            {handleNil(elem.buyersItemId)}
                        </div>
                    )}
                </div>
            ),
            serviceCategory: handleNil(elem.serviceCategory, (v) => (
                <Cell>{typeof v === 'object' ? v.label : v}</Cell>
            )),
            qty: handleNil(elem.qty, (v) => <Cell>{v}</Cell>),
            price: handleNil(elem.price, (v) => <Cell>{v}</Cell>),
            charges: (
                <>
                    {!elem.charges && <Cell>—</Cell>}
                    {elem.charges && (
                        <ChargesCell>
                            <h4>{t(`radio.${elem.charges?.isCharge ? '1' : '2'}`)}</h4>
                            {elem.charges?.reason && (
                                <div>
                                    <span>
                                        {translation(`${BASE_LOCALE}.invoiceLines.reason.label`)}
                                    </span>
                                    <span>{elem.charges?.reason}</span>
                                </div>
                            )}
                            <div>
                                {elem.charges?.baseAmount && (
                                    <div>
                                        <span>
                                            {translation(
                                                `${BASE_LOCALE}.invoiceLines.baseAmount.label`,
                                            )}
                                        </span>
                                        <span>{elem.charges?.baseAmount}</span>
                                    </div>
                                )}
                                {elem.charges?.amountXaf && (
                                    <div>
                                        <span>
                                            {translation(
                                                `${BASE_LOCALE}.taxesTotal.charges.amount`,
                                            )}
                                        </span>
                                        <span>
                                            {formatNumberWithFraction(elem.charges?.amountXaf, {
                                                minimumFractionDigits: 1,
                                                maximumFractionDigits: 1,
                                                useGrouping: false,
                                            })}
                                        </span>
                                    </div>
                                )}
                                {elem.charges?.ratePercent && (
                                    <div>
                                        <span>
                                            {translation(`${BASE_LOCALE}.taxesTotal.rate.label`)}
                                        </span>
                                        <span>{elem.charges?.ratePercent ?? ''}%</span>
                                    </div>
                                )}
                            </div>
                        </ChargesCell>
                    )}
                </>
            ),
            lines: handleNil(elem.lines, (v) => <Cell>{v}</Cell>),
            vatCategory: handleNil(elem.vatCategory?.value, (v) => (
                <Cell>{VAT_CATEGORY.find((c) => c.value === v)?.label ?? v ?? '—'}</Cell>
            )),
            vat: handleNil(elem.vat, (v) => <Cell>{v}</Cell>),
            lineAmountWVat: handleNil(elem.lineAmountWVat, (v) => <Cell>{v}</Cell>),
        }));
    }, [invoiceLines, translation]);

    const taxColumnSettings: GridFieldConfig[] = useMemo(
        () => [
            {
                id: 'taxCategory',
                header: translation(`${BASE_LOCALE}.taxesTotal.taxCategory.label`),
            },
            {
                id: 'rate',
                header: translation(`${BASE_LOCALE}.taxesTotal.vat.label`),
            },
            {
                id: 'taxableAmount',
                header: `${translation(
                    `${BASE_LOCALE}.taxesTotal.taxableAmount.label`,
                )} (${CURRENCY})`,
            },
            {
                id: 'taxAmount',
                header: `${translation(`${BASE_LOCALE}.taxesTotal.taxAmount.label`)} (${CURRENCY})`,
            },
        ],
        [i18n.language],
    );
    const taxColumns = useMemo(() => mapTableColumns(taxColumnSettings), [
        taxColumnSettings,
        i18n.language,
    ]);

    const taxData = useMemo(() => {
        if (!taxesAndTotal.data.calculationsByTaxCategory) return;
        return taxesAndTotal.data.calculationsByTaxCategory.map((elem) => ({
            taxCategory: handleNil(elem.taxCategoryDisplay, (v) => <Cell>{v ?? '—'}</Cell>),
            rate: handleNil(elem.rate, (v) => <Cell>{v}</Cell>),
            taxableAmount: handleNil(elem.taxableAmount, (v) => <Cell>{v}</Cell>),
            taxAmount: handleNil(elem.taxAmount, (v) => <Cell>{v}</Cell>),
        }));
    }, [taxesAndTotal]);

    return (
        <Wrapper>
            <Row>
                <Container
                    style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                >
                    <Title vertical={IS_NG}>
                        {translation(`${BASE_LOCALE}.title`)}
                        <span>{infoValues.irn}</span>
                    </Title>
                    {IS_NG && (
                        <BlockQR>
                            <span>{translation('invoicePrint.verifyQr')}</span>
                            {QR && <img src={`data:image/png;base64,${QR}`} alt="QR" />}
                        </BlockQR>
                    )}
                </Container>
                <Container>
                    <Header>{translation(`${GENERAL_INFO}.headlines.generalInformation`)}</Header>
                    <List>
                        <div>
                            <span>{translation(`${GENERAL_INFO}.inputs.issueDate`)}</span>
                            <span>{formatDate(infoValues.issueDate)}</span>
                        </div>
                        <div>
                            <span>{translation(`${GENERAL_INFO}.inputs.dueDate`)}</span>
                            <span>{formatDate(infoValues.dueDate)}</span>
                        </div>
                    </List>
                </Container>
            </Row>
            <Row>
                <Container>
                    <Header>
                        {translation(`${GENERAL_INFO}.headlines.supplier`)}
                        <Label>{infoValues.companyNameAndTIN}</Label>
                    </Header>
                    <List>
                        <div>
                            <span>
                                {translation(`${GENERAL_INFO}.inputs.tin`)} ({COUNTRY})
                            </span>
                            <span>{formatValue(infoValues.companyTIN)}</span>
                        </div>
                        <div>
                            <span>{translation(`${GENERAL_INFO}.inputs.supplierEmail`)}</span>
                            <span>{formatValue(infoValues.supplierEmail)}</span>
                        </div>
                        <div>
                            <span>{translation(`${GENERAL_INFO}.inputs.supplierPhoneNumber`)}</span>
                            <span>{formatValue(infoValues.supplierPhoneNumber)}</span>
                        </div>
                        <div>
                            <span>{translation(`${GENERAL_INFO}.inputs.supplierStreetName`)}</span>
                            <span>{formatValue(infoValues.supplierStreetName)}</span>
                        </div>
                    </List>
                </Container>
                <Divider />
                <Container>
                    <Header>
                        {translation(`${GENERAL_INFO}.headlines.customer`)}
                        <Label>{infoValues.customerCompanyNameOrTIN?.label}</Label>
                    </Header>
                    <List>
                        <div>
                            <span>
                                {translation(`${GENERAL_INFO}.inputs.tin`)} ({COUNTRY})
                            </span>
                            <span>{formatValue(infoValues.customerCompanyNameOrTIN?.value)}</span>
                        </div>
                        <div>
                            <span>{translation(`${GENERAL_INFO}.inputs.supplierEmail`)}</span>
                            <span>{formatValue(infoValues.customerEmail)}</span>
                        </div>
                        <div>
                            <span>{translation(`${GENERAL_INFO}.inputs.supplierPhoneNumber`)}</span>
                            <span>{formatValue(infoValues.customerPhoneNumber)}</span>
                        </div>
                        <div>
                            <span>{translation(`${GENERAL_INFO}.inputs.supplierStreetName`)}</span>
                            <span>{formatValue(infoValues.customerAddress)}</span>
                        </div>
                    </List>
                </Container>
            </Row>
            <Row>
                <Container>
                    <Header>
                        {translation(`${BASE_LOCALE}.documents.headlines.relatedDocuments`)}
                    </Header>
                    <List>
                        <div>
                            <span>{translation(`${BASE_LOCALE}.documents.inputs.from`)}</span>
                            <span>{formatDate(pageValues.periodFrom)}</span>
                        </div>
                        <div>
                            <span>{translation(`${BASE_LOCALE}.documents.inputs.due`)}</span>
                            <span>{formatDate(pageValues.periodTo)}</span>
                        </div>
                        <div>
                            <span>{translation(`${BASE_LOCALE}.documents.inputs.pointDate`)}</span>
                            <span>{formatDate(pageValues.taxPointDate)}</span>
                        </div>
                    </List>
                </Container>
                <Divider style={{margin: '24px 20px'}} />
                <Container>
                    <List style={{marginTop: '54px'}}>
                        <div>
                            <span>
                                {translation(`${BASE_LOCALE}.documents.inputs.buyerReference`)}
                            </span>
                            <span>{formatValue(pageValues.buyerReference)}</span>
                        </div>
                        <div>
                            <span>
                                {translation(
                                    `${BASE_LOCALE}.documents.inputs.purchaseOrderReference`,
                                )}
                            </span>
                            <span>{formatValue(pageValues.purchaseOrderReference)}</span>
                        </div>
                        <div>
                            <span>
                                {translation(`${BASE_LOCALE}.documents.inputs.salesOrderReference`)}
                            </span>
                            <span>{formatValue(pageValues.salesOrderReference)}</span>
                        </div>
                        <div>
                            <span>{translation(`${BASE_LOCALE}.documents.inputs.Contract`)}</span>
                            <span>{formatValue(pageValues.contractDocumentReference)}</span>
                        </div>
                    </List>
                </Container>
            </Row>
            <Row>
                <Container>
                    <Header>{translation(`${BASE_LOCALE}.invoiceLines.invoiceLines`)}</Header>
                    <Table
                        hideSetting={true}
                        isLoading={false}
                        cellContainerSettings={'height: 100%; width: 100%;'}
                        columns={invoiceColumns}
                        data={invoicesData}
                    />
                </Container>
            </Row>
            {!!taxesAndTotal.data.allowanceCharge?.length && (
                <Row>
                    <Container>
                        <Header>
                            {translation(`${BASE_LOCALE}.taxesTotal.headlines.charges`)}
                        </Header>
                        <ChargesList print />
                    </Container>
                </Row>
            )}
            <Row>
                <Container>
                    <Header>{translation(`${BASE_LOCALE}.taxesTotal.headlines.taxes`)}</Header>
                    <Table
                        hideSetting={true}
                        isLoading={false}
                        cellContainerSettings={'height: 100%; width: 100%;'}
                        columns={taxColumns}
                        data={taxData}
                    />
                    {taxesAndTotal.data.taxTotal && (
                        <Summary style={{marginTop: '20px'}}>
                            <span>{translation(`${BASE_LOCALE}.taxesTotal.taxTotal.label`)}</span>
                            <span>
                                {formatNumberWithFraction(taxesAndTotal.data.taxTotal)} {CURRENCY}
                            </span>
                        </Summary>
                    )}
                </Container>
            </Row>
            <Row>
                <Container>
                    <Header>{translation(`${BASE_LOCALE}.taxesTotal.headlines.totals`)}</Header>
                    <List summary>
                        <div>
                            <span>
                                {translation(
                                    `${BASE_LOCALE}.taxesTotal.charges.lineExtensionAmount.label`,
                                )}
                            </span>
                            <span>
                                {formatNumberWithFraction(taxesAndTotal.data.lineExtensionAmount)}
                            </span>
                            <span>{CURRENCY}</span>
                        </div>
                        <div>
                            <span>
                                {translation(
                                    `${BASE_LOCALE}.taxesTotal.charges.taxExclusiveAmount_1.label`,
                                )}
                            </span>
                            <span>
                                {formatNumberWithFraction(taxesAndTotal.data.taxExclusiveAmount)}
                            </span>
                            <span>{CURRENCY}</span>
                        </div>
                        <div>
                            <span>
                                {translation(
                                    `${BASE_LOCALE}.taxesTotal.charges.taxExclusiveAmount_2.label`,
                                )}
                            </span>
                            <span>
                                {formatNumberWithFraction(taxesAndTotal.data.taxInclusiveAmount)}
                            </span>
                            <span>{CURRENCY}</span>
                        </div>
                        <div>
                            <span>{translation('invoicePrint.allowanceTotalAmount')}</span>
                            <span>{formatNumberWithFraction(null)}</span>
                            <span>{CURRENCY}</span>
                        </div>
                        <div>
                            <span>{translation('invoicePrint.chargeTotalAmount')}</span>
                            <span>{formatNumberWithFraction(null)}</span>
                            <span>{CURRENCY}</span>
                        </div>
                        <div>
                            <span>
                                {translation(
                                    `${BASE_LOCALE}.taxesTotal.charges.amountDueForPayment.label`,
                                )}
                            </span>
                            <span>
                                {formatNumberWithFraction(taxesAndTotal.data.prepaidAmount)}
                            </span>
                            <span>{CURRENCY}</span>
                        </div>
                        <div style={{borderBottom: '1px solid #E2E2E2', paddingBottom: 20}}>
                            <span>
                                {translation(
                                    `${BASE_LOCALE}.taxesTotal.charges.roundingAmount.label`,
                                )}
                            </span>
                            <span>
                                {formatNumberWithFraction(taxesAndTotal.data.payableRoundingAmount)}
                            </span>
                            <span>{CURRENCY}</span>
                        </div>
                    </List>
                    <Summary style={{paddingTop: 20, justifyContent: 'space-between'}}>
                        <span style={{width: '50%'}}>
                            {translation(`${BASE_LOCALE}.taxesTotal.charges.payableAmount.label`)}
                        </span>
                        <span style={{width: '160px'}}>
                            {formatNumberWithFraction(taxesAndTotal.data.payableAmount)}
                        </span>
                        <span style={{width: 'fit-content'}}>{CURRENCY}</span>
                    </Summary>
                </Container>
                <Divider />
                <Container>
                    <Header>{translation(`${GENERAL_INFO}.headlines.supplierBankDetails`)}</Header>
                    <List>
                        <div>
                            <span style={{fontSize: 24}}>
                                {translation(`${GENERAL_INFO}.inputs.iban`)}
                            </span>
                            <span style={{fontSize: 24}}>{infoValues.iban || '—'}</span>
                        </div>
                        <div>
                            <span style={{fontSize: 24}}>
                                {translation(`${GENERAL_INFO}.inputs.bic`)}
                            </span>
                            <span style={{fontSize: 24}}>{infoValues.bic || '—'}</span>
                        </div>
                    </List>
                </Container>
            </Row>
        </Wrapper>
    );
};
